import { useEffect, type RefObject } from "react";
import { invoke } from "@tauri-apps/api/core";
import type { ComposerFieldHandle } from "@/components/panels/standalone-bar-field";
import { isTauri } from "@/lib/is-tauri";

/** Bring this overlay window in front with the keyboard. A plain `setFocus()`
 * is refused on Windows after a shortcut, whose keys went to another app. */
export function activateSummonedInputWindow(): Promise<void> {
  return invoke("focus_desktop_window");
}

export function useSummonedInputFocus(
  fieldRef: RefObject<ComposerFieldHandle | null>,
  summonKey: string | number,
  visible: boolean,
) {
  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    const focusEditor = () => {
      const field = fieldRef.current;
      if (cancelled || !field) return;
      field.focus({ preventScroll: true });
      // A restored draft goes on where it stopped; focusing an editable
      // alone puts the caret before its first character.
      const end = field.value.length;
      field.setSelectionRange(end, end);
    };
    focusEditor();
    // Native activation is asynchronous. Establish the WebKit editor responder
    // after it completes, without waiting on paused animation frames.
    if (isTauri()) void activateSummonedInputWindow().then(focusEditor).catch(() => {});
    return () => { cancelled = true; };
  }, [fieldRef, summonKey, visible]);
}
